
import React, {useEffect, useRef, useState} from "react";
import {
    FileUploadContainer,
    FormField,
    DragDropText,
    UploadFileBtn,
    FilePreviewContainer,
    ImagePreview,
    PreviewContainer,
    PreviewList,
    FileMetaData,
    RemoveFileIcon,
    InputLabel
} from "./dragdrop-styles.jsx"

const KILO_BYTES_PER_BYTE = 1000;
const DEFAULT_MAX_FILE_SIZE_IN_BYTES = 50000000000000;

const convertNestedObjectToArray = (nestedObj) =>
    Object.keys(nestedObj).map((key) => nestedObj[key]);

const convertBytesToKB = (bytes) => Math.round(bytes / KILO_BYTES_PER_BYTE);

/**
 * DragDrop - File upload component with drag-and-drop support and preview
 *
 * Provides a user-friendly interface for uploading files with:
 * - Drag-and-drop zone with visual feedback
 * - File size validation (configurable maxFileSizeInBytes)
 * - Preview grid showing selected files with metadata
 * - Remove individual files or clear all (via del prop)
 * - Supports single or multiple file uploads
 *
 * Uses styled-components for consistent styling and Backend API for uploads.
 *
 * @param {Object} props
 * @param {string} props.label - Label displayed above the drop zone (required)
 * @param {function} props.updateFilesCb - Callback receiving array of selected File objects
 * @param {number} props.maxFileSizeInBytes - Maximum allowed file size (default: 50TB)
 * @param {boolean} props.del - When true, clears all files (controlled prop)
 * @param {function} props.delAll - Callback to reset del state after clearing
 * @param {...Object} otherProps - Additional props passed to FormField (e.g., multiple, accept)
 * @returns {React.ReactElement} Rendered drag-and-drop upload component
 */
const DragDrop = ({
                        label,
                        updateFilesCb,
                        maxFileSizeInBytes = DEFAULT_MAX_FILE_SIZE_IN_BYTES,
                        del, delAll,
                        ...otherProps
                    }) => {
    let fileInputField = useRef(null);
    const [files, setFiles] = useState({});

    const handleUploadBtnClick = () => {
        fileInputField.current.click();
    };



    const addNewFiles = (newFiles) => {
        for (let file of newFiles) {
            if (file.size <= maxFileSizeInBytes) {
                if (!otherProps.multiple) {
                    return { file };
                }
                files[file.name] = file;
            }
        }
        return { ...files };
    };

    const callUpdateFilesCb = (files) => {
        const filesAsArray = convertNestedObjectToArray(files);
        updateFilesCb(filesAsArray);
    };

    // useEffect(() => {
    //     if(del===true) {
    //         //setFiles({})
    //         fileInputField = null;
    //         updateFilesCb(null);
    //         callUpdateFilesCb({});
    //         del=false;
    //
    //     }
    // },[del]);

   // const del = () => callUpdateFilesCb({});

    const handleNewFileUpload = (e) => {
        const { files: newFiles } = e.target;
        if (newFiles.length) {
            let updatedFiles = addNewFiles(newFiles);
            setFiles(updatedFiles);
            callUpdateFilesCb(updatedFiles);
        }
    };

    const removeFile = (fileName) => {
        delete files[fileName];
        setFiles({ ...files });
        callUpdateFilesCb({ ...files });
    };

    useEffect(() => {
        if(del===true) {
            document.getElementById('filesInput').value = null;
            setFiles({});
            callUpdateFilesCb({});
            delAll(false);
        }
    },[del]);

    return (
        <>
            <FileUploadContainer>
                <InputLabel>{label}</InputLabel>
                <DragDropText>Drag and drop inside this box or click below</DragDropText>
                <UploadFileBtn type="button" onClick={handleUploadBtnClick}>
                    <i className="fas fa-file-upload" />
                    <span> Select {otherProps.multiple ? "files" : "a file"} to upload</span>
                </UploadFileBtn>
                <FormField
                    id="filesInput"
                    type="file"
                    ref={fileInputField}
                    onChange={handleNewFileUpload}
                    title=""
                    value=""
                    {...otherProps}
                />
            </FileUploadContainer>
            <FilePreviewContainer>
                <span>To Upload</span>
                <PreviewList id="pvList">
                    {Object.keys(files).map((fileName, index) => {
                        let file = files[fileName];
                        let isImageFile = file.type.split("/")[0] === "image";
                        return (
                            <PreviewContainer key={fileName}>
                                <div className="aspect11">
                                    {isImageFile && (
                                        <ImagePreview
                                            src={URL.createObjectURL(file)}
                                            alt={`file preview ${index}`}
                                        />
                                    )}
                                    <FileMetaData isImageFile={isImageFile}>
                                        <span>{file.name}</span>
                                        <aside>
                                            <span>{convertBytesToKB(file.size)} kb</span>
                                            <RemoveFileIcon
                                                className="fas fa-trash-alt"
                                                onClick={() => removeFile(fileName)}
                                            />
                                        </aside>
                                    </FileMetaData>
                                </div>
                            </PreviewContainer>
                        );
                    })}
                </PreviewList>
            </FilePreviewContainer>
        </>
    );
};

export default DragDrop;
